// B-1: shared card shell for the live + upcoming variants.
//
// The WHOLE card is one link element — no Watch button anywhere. There is
// no in-app watch view (the legacy button opened YouTube directly), so the
// card is an external link to the stream, opened in a new tab. Hover and
// focus treatment are defined once here and never re-declared per variant.
// Separation inside the card is whitespace and type hierarchy only — no
// horizontal dividers.
//
// All colors/radii/shadows/motion come from the browse-* tokens in
// tailwind.config.js (raw hex fails the designTokens jest check).
import React from 'react';

/**
 * @param {Object} props
 * @param {?string} props.watchUrl  external watch link for the stream
 * @param {React.ReactNode} props.thumbnail  content of the 16:9 slot
 *   (variants add their own badges/overlays; any <img> is object-fit cover)
 * @param {React.ReactNode} props.children  body content (title, meta row, …)
 * @param {boolean} [props.mobileRow]  SOR-625: below sm the card renders as
 *   a compact row (120px thumb left, body right) and restores the full-width
 *   card treatment at sm:. Only the upcoming variant opts in.
 */
const StreamCard = ({ watchUrl, thumbnail, children, onClick, mobileRow = false }) => (
  <a
    href={watchUrl}
    target="_blank"
    rel="noopener noreferrer"
    onClick={onClick}
    className={
      // gap-[12px]/gap-[4px] arbitrary spellings: Bootstrap's .gap-3/.gap-1
      // !important utilities would override both the mobile value and the
      // sm: restore
      (mobileRow
        ? 'relative flex items-center gap-[12px] p-2.5 sm:block sm:p-[0px] '
        : 'block ') +
      'no-underline bg-browse-surface border border-browse-border rounded-card overflow-hidden cursor-pointer transition duration-180 ease-[ease] hover:-translate-y-0.5 hover:shadow-card-hover hover:border-browse-border-card-hover focus-visible:outline-none focus-visible:border-browse-accent focus-visible:ring-2 focus-visible:ring-browse-accent'
    }
  >
    {/* inline aspect-ratio: the @tailwindcss/aspect-ratio plugin supersedes
        the core aspect-video utility, so the class never reaches the CSS —
        4:3 source thumbs (YouTube sddefault) were sizing their own slots */}
    <div
      style={{ aspectRatio: '16 / 9' }}
      className={
        (mobileRow ? 'w-[120px] shrink-0 rounded-btn sm:w-auto sm:rounded-none ' : '') +
        'relative overflow-hidden [&_img]:h-full [&_img]:w-full [&_img]:object-cover'
      }
    >
      {thumbnail}
    </div>
    <div
      className={
        'flex flex-col ' +
        (mobileRow
          ? 'min-w-0 flex-1 gap-[4px] pr-11 sm:gap-2.25 sm:pt-3 sm:px-3.25 sm:pb-3.25'
          : 'gap-2.25 pt-3 px-3.25 pb-3.25')
      }
    >
      {children}
    </div>
  </a>
);

export default StreamCard;
